import { bbox as turfBbox } from '@turf/bbox';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { isEqual } from 'lodash-es';
import {
  LngLatBounds,
  type LngLatBoundsLike,
  type Map,
  type StyleSpecification,
} from 'maplibre-gl';
import type { StyleState } from '@/components/map/utils/layer-style-helpers';
import type { DataSourceConfig } from '@/components/maplibre';
import { DATA_SOURCE_TYPES, MAP_STYLES, OTHER_CATEGORY_COLOR } from '@/components/maplibre/consts';
import { GEOMETRY_TYPES } from '@/components/maplibre/consts';
import {
  GeoJSONGeometryType,
  GeoJsonCoordinates,
  type LayerBbox,
  type LayerStats,
  ReadonlyTilesetDataSourceConfig,
} from '@/components/maplibre/types';
import { MAX_RENDERED_CATEGORIES, OTHERS_CATEGORY } from '@/lib/constants/category-constants';
import { MAP_LAYERS_PANEL_RIGHT_OFFSET } from '@/lib/constants/map-layout';
import { LAYER_GEOMETRY_TYPE } from '@/lib/db-constants';
import type { Layer, LayerGeometryType } from '@/lib/db-row-types';
import { DEFAULT_LAYERS_PANEL_WIDTH } from '@/lib/stores/slices/mapPanelsUI.slice';
import {
  COLOR_SCALES,
  COLOR_SCHEMES,
  categoryColorAt,
  generateGradientColors,
  isGradationScheme,
  sortCategoryValues,
} from '@/utils/colors';
import { getFieldStatsByColumnName, topCategoryValues } from '@/utils/layer-sources/property-stats';
import { resolveColumnName } from '@/utils/layer-sources/style-v2';
import { getAzureSatelliteStyle } from './azure-maps-style';

/**
 * Checks if a MapLibre map instance has been removed/destroyed.
 * After calling map.remove(), the internal style object is destroyed,
 * causing methods like getLayer() to throw errors.
 *
 * @param map - The MapLibre map instance to check
 * @returns true if the map has been removed and is no longer usable
 */
export function isMapRemoved(map: Map): boolean {
  // MapLibre sets style to undefined when map.remove() is called
  // We check this to avoid calling methods on a destroyed map
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (map as any).style === undefined;
}

export function getLayerId(
  sourceId: string,
  layerType: 'circle' | 'fill' | 'line' | 'outline' | 'symbol' | 'symbol-icon' | 'raster' = 'circle'
): string {
  return `${sourceId}-${layerType}`;
}

export function getSourceId(sourceId: string): string {
  return `source-${sourceId}`;
}

/**
 * Normalizes a style value to a standard style key ('streets' or 'satellite').
 * Handles legacy Mapbox URLs and old key formats.
 *
 * @param style - The style value to normalize (could be a key, URL, or legacy value)
 * @returns Normalized style key ('streets' or 'satellite')
 */
export function normalizeStyleKey(
  style: string | undefined
): (typeof MAP_STYLES)[keyof typeof MAP_STYLES] {
  if (!style) return MAP_STYLES.streets;

  if (style.includes('mapbox://styles/mapbox/streets')) {
    return MAP_STYLES.streets;
  }
  if (style.includes('mapbox://styles/mapbox/satellite') || style === 'azure-satellite') {
    return MAP_STYLES.satellite;
  }

  return style === MAP_STYLES.streets || style === MAP_STYLES.satellite
    ? style
    : MAP_STYLES.streets;
}

/**
 * Resolves a map style key to its actual implementation.
 * This creates a facade where only 'streets' or 'satellite' keys are used,
 * and the actual provider (Mapbox, Azure, etc.) is managed by the code.
 *
 * @param styleKey - The style key ('streets' or 'satellite')
 * @returns The resolved style (Mapbox URL for streets, Azure style object for satellite)
 */
export function resolveMapStyle(styleKey: string): string | StyleSpecification {
  const normalizedKey = normalizeStyleKey(styleKey);

  if (normalizedKey === MAP_STYLES.satellite) {
    const azureKey = process.env.NEXT_PUBLIC_AZURE_MAPS_KEY;
    if (!azureKey) {
      console.warn('NEXT_PUBLIC_AZURE_MAPS_KEY not found, falling back to streets style');
      return 'mapbox://styles/mapbox/streets-v12';
    }
    return getAzureSatelliteStyle(azureKey);
  }

  return 'mapbox://styles/mapbox/streets-v12';
}

/**
 * Parses Mapbox GL JS URL hash format: #zoom/lat/lng
 * Returns null if hash is invalid or doesn't exist
 */
export function parseMapHash(hash: string): {
  zoom: number;
  center: [number, number];
} | null {
  if (!hash || !hash.startsWith('#')) return null;

  const parts = hash.slice(1).split('/');
  if (parts.length < 3) return null;

  const zoom = parseFloat(parts[0]);
  const lat = parseFloat(parts[1]);
  const lng = parseFloat(parts[2]);

  if (isNaN(zoom) || isNaN(lat) || isNaN(lng)) return null;

  return {
    zoom,
    center: [lng, lat], // Mapbox uses [lng, lat] order
  };
}

export function isCommonLayer(layer: Layer) {
  return layer.workspace_id === null;
}

export const getGeometryTypes = (data: FeatureCollection): Array<LayerGeometryType> => {
  const geometryTypes = new Set<LayerGeometryType>();

  if (data?.features) {
    data.features.forEach((feature: Feature) => {
      if (feature.geometry?.type) {
        const layerGeometryType = mapGeoJsonTypeToLayerGeometryType(
          feature.geometry.type as GeoJSONGeometryType
        );
        if (layerGeometryType) {
          geometryTypes.add(layerGeometryType);
        }
      }
    });
  }

  return Array.from(geometryTypes);
};

const mapGeoJsonTypeToLayerGeometryType = (
  geoJsonType: GeoJSONGeometryType
): LayerGeometryType | null => {
  switch (geoJsonType) {
    case GEOMETRY_TYPES.POINT:
    case GEOMETRY_TYPES.MULTI_POINT:
      return LAYER_GEOMETRY_TYPE.POINT;
    case GEOMETRY_TYPES.POLYGON:
    case GEOMETRY_TYPES.MULTI_POLYGON:
      return LAYER_GEOMETRY_TYPE.POLYGON;
    case GEOMETRY_TYPES.LINE_STRING:
    case GEOMETRY_TYPES.MULTI_LINE_STRING:
      return LAYER_GEOMETRY_TYPE.LINE_STRING;
    default:
      return null;
  }
};

export const extractCoords = (coords: GeoJsonCoordinates): [number, number][] => {
  if (typeof coords[0] === 'number') {
    // Single coordinate pair [lng, lat]
    return [coords as [number, number]];
  } else if (Array.isArray(coords[0])) {
    // Nested array, recursively extract
    return (coords as any[]).flatMap((item: GeoJsonCoordinates) => extractCoords(item));
  }
  return [];
};

export const addFeaturesToBounds = (features: Feature[], bounds: LngLatBounds): void => {
  features.forEach(feature => {
    if (feature?.geometry) {
      const { coordinates } = feature.geometry as any;
      if (coordinates) {
        const coordPairs = extractCoords(coordinates as GeoJsonCoordinates);
        coordPairs.forEach(coord => bounds.extend(coord));
      }
    }
  });
};

export const getLayerLegendColors = (
  styleState: StyleState,
  dataSources: DataSourceConfig[],
  layer: Layer
) => {
  const colorMap: Record<string, string> = {};
  if (!styleState.colorLogicEnabled) return colorMap;

  const isGradation = isGradationScheme(styleState.colorLogicScheme);
  const scheme = isGradation
    ? COLOR_SCALES.find(s => s.name === styleState.colorLogicScheme)
    : COLOR_SCHEMES.find(s => s.name === styleState.colorLogicScheme);
  if (!scheme) return colorMap;

  const dataSource = dataSources.find(ds => ds.id === layer.id);
  if (!dataSource) return colorMap;

  const columnName = resolveColumnName(
    styleState.colorLogicColumn,
    dataSource.columnMetadata,
    layer.stats as unknown as LayerStats | undefined
  );
  if (!columnName) return colorMap;

  // Selection must match the paint expression builders exactly, so legend
  // entries and map colors stay aligned: first-N in feature order for
  // feature-derived branches, count-desc top-N for the stats-derived branch.
  let renderedValues: string[] = [];
  let totalDistinct = 0;

  if (dataSource.type === DATA_SOURCE_TYPES.GEOJSON) {
    const collected = new Set<string>();
    const features = dataSource.data.features || [];
    features.forEach(feature => {
      const value = feature.properties?.[columnName];
      // Mirror the paint path's filter (it also skips empty strings).
      if (value != null && value !== '') {
        collected.add(String(value));
      }
    });
    renderedValues = Array.from(collected).slice(0, MAX_RENDERED_CATEGORIES);
    totalDistinct = collected.size;
  } else if (dataSource.type === DATA_SOURCE_TYPES.TILESET) {
    const collected = new Set<string>();
    Object.values(dataSource.featureData).forEach(properties => {
      if (properties && properties[columnName] != null) {
        collected.add(String(properties[columnName]));
      }
    });
    renderedValues = Array.from(collected).slice(0, MAX_RENDERED_CATEGORIES);
    totalDistinct = collected.size;
  } else if (dataSource.type === DATA_SOURCE_TYPES.READONLY_TILESET) {
    const readonlyDataSource = dataSource as ReadonlyTilesetDataSourceConfig;
    const stats = readonlyDataSource.stats;
    const field = stats?.fields ? getFieldStatsByColumnName(columnName, stats) : null;
    if (field?.values) {
      renderedValues = topCategoryValues(field.values, MAX_RENDERED_CATEGORIES);
      totalDistinct = Math.max(field.count_distinct ?? 0, Object.keys(field.values).length);
    }
  }

  const uniqueValues = new Set(renderedValues);
  if (totalDistinct > uniqueValues.size) {
    uniqueValues.add(OTHERS_CATEGORY);
  }

  if (isGradation) {
    // For gradation schemes: sort categories and generate evenly spaced gradient colors
    const sortedValues = sortCategoryValues(uniqueValues);
    const gradientColors = generateGradientColors(
      scheme.values[0],
      scheme.values[1],
      sortedValues.length
    );

    sortedValues.forEach((value, index) => {
      if (value === OTHERS_CATEGORY) {
        colorMap[value] = OTHER_CATEGORY_COLOR;
      } else {
        colorMap[value] =
          styleState.customColors?.find(c => c.category === value)?.color ?? gradientColors[index];
      }
    });
  } else {
    // For category schemes: one deterministic color per index.
    // When a style carries customColors, the paint expressions only emit branches
    // for values found there and send the rest to the Others color -- so the
    // legend must not fall back to a scheme swatch, or it would promise a color
    // the map never paints.
    const hasCustomColors = !!styleState.customColors?.length;
    Array.from(uniqueValues).forEach((value, index) => {
      if (value === OTHERS_CATEGORY) {
        colorMap[value] = OTHER_CATEGORY_COLOR;
        return;
      }
      const custom = styleState.customColors?.find(c => c.category === value)?.color;
      colorMap[value] =
        custom ?? (hasCustomColors ? OTHER_CATEGORY_COLOR : categoryColorAt(scheme.values, index));
    });
  }

  return colorMap;
};

export const hasDataChanged = (prev: DataSourceConfig, current: DataSourceConfig): boolean => {
  if (prev.type !== current.type) return true;

  if (current.type === DATA_SOURCE_TYPES.GEOJSON && prev.type === DATA_SOURCE_TYPES.GEOJSON) {
    return !isEqual(prev.data, current.data);
  }

  if (current.type === DATA_SOURCE_TYPES.TILESET && prev.type === DATA_SOURCE_TYPES.TILESET) {
    return !isEqual(prev.featureData, current.featureData);
  }

  if (
    current.type === DATA_SOURCE_TYPES.READONLY_TILESET &&
    prev.type === DATA_SOURCE_TYPES.READONLY_TILESET
  ) {
    return prev.revisionId !== current.revisionId;
  }

  return false;
};

/**
 * Returns true when the layer bbox is completely outside the map's current viewport.
 * Fail-open: returns false when bbox is null or malformed so callers never
 * incorrectly hide content.
 */
export function isBboxOutOfViewport(
  bbox: LayerBbox | null | undefined,
  viewportBounds: { west: number; east: number; south: number; north: number }
): boolean {
  if (!bbox?.coordinates?.[0] || bbox.coordinates[0].length < 4) return false;

  const ring = bbox.coordinates[0];
  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  for (const [lng, lat] of ring) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  // AABB disjoint test
  return (
    maxLng < viewportBounds.west ||
    minLng > viewportBounds.east ||
    maxLat < viewportBounds.south ||
    minLat > viewportBounds.north
  );
}

export const zoomToBounds = (
  map: Map,
  bounds: LngLatBoundsLike,
  panelWidth: number = DEFAULT_LAYERS_PANEL_WIDTH
) => {
  const rightPadding = MAP_LAYERS_PANEL_RIGHT_OFFSET + panelWidth + 50;
  map.fitBounds(bounds, {
    padding: { left: 50, top: 50, bottom: 50, right: rightPadding },
    maxZoom: 16,
    maxDuration: 1000,
  });
};

/**
 * Zooms the map to fit `geometry`. Returns `false` and no-ops when the
 * geometry produces a non-finite bbox (NaN coords, empty rings, etc.) so
 * callers can surface user feedback instead of letting `fitBounds` collapse
 * to nowhere.
 */
export const zoomToGeometry = (
  map: Map,
  geometry: Geometry,
  panelWidth: number = DEFAULT_LAYERS_PANEL_WIDTH
): boolean => {
  const [minX, minY, maxX, maxY] = turfBbox({
    type: 'Feature',
    geometry,
    properties: {},
  }) as [number, number, number, number];
  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return false;
  }
  // Single point — synthesise a tiny bounding box around it so fitBounds
  // produces a reasonable zoom level instead of collapsing.
  const dx = maxX - minX;
  const dy = maxY - minY;
  const isDegenerate = dx === 0 && dy === 0;
  const padDeg = 0.005;
  const bounds: LngLatBoundsLike = isDegenerate
    ? [
        [minX - padDeg, minY - padDeg],
        [maxX + padDeg, maxY + padDeg],
      ]
    : [
        [minX, minY],
        [maxX, maxY],
      ];
  zoomToBounds(map, bounds, panelWidth);
  return true;
};
